/**
 * Offline cache coherence for request-scoped location tags (#999).
 */

import { ApiError } from './api';
import { offlineCache, type OfflineLocationTagOperation } from './offlineCache';

import type { LocationTag } from '../types/locationTag';

let offlineMutationChain: Promise<void> = Promise.resolve();

/** Serialize queue + cache read/modify/write operations to prevent lost updates. */
export function runSerializedLocationTagOfflineMutation<T>(
  mutation: () => Promise<T>,
): Promise<T> {
  const result = offlineMutationChain.then(mutation, mutation);
  offlineMutationChain = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

/** Allocate a collision-safe negative draft id while under the mutation lock. */
export function nextLocationTagDraftId(tags: readonly LocationTag[]): number {
  const smallest = tags.reduce(
    (current, tag) => (tag.id < current ? tag.id : current),
    0,
  );
  return smallest < 0 ? smallest - 1 : -1;
}

export function isDuplicateLocationTagName(
  tags: LocationTag[],
  name: string,
  excludeTagId?: number,
): boolean {
  const normalized = name.trim().toLowerCase();
  return tags.some(
    tag =>
      tag.id !== excludeTagId && tag.name.trim().toLowerCase() === normalized,
  );
}

export async function getCachedLocationTagList(
  requestId: string,
): Promise<LocationTag[]> {
  return offlineCache.getCachedLocationTags(requestId);
}

export async function mirrorLocationTagsToOfflineCache(
  requestId: string,
  tags: LocationTag[],
): Promise<void> {
  await offlineCache.setCachedLocationTags(requestId, tags);
}

export async function getPendingLocationTagOpsForRequest(
  requestId: string,
): Promise<OfflineLocationTagOperation[]> {
  const pending = await offlineCache.getPendingLocationTagOperations();
  return pending.filter(op => String(op.requestId) === String(requestId));
}

export function applyPendingLocationTagOperations(
  requestId: string,
  base: LocationTag[],
  ops: OfflineLocationTagOperation[],
): LocationTag[] {
  const ordered = [...ops].sort((a, b) => a.timestamp - b.timestamp);
  let result = [...base];
  const requestIdNum = parseInt(requestId, 10);

  for (const op of ordered) {
    switch (op.operation) {
      case 'create': {
        if (!op.data?.name || op.tagId === undefined) {
          break;
        }
        if (result.some(tag => tag.id === op.tagId)) {
          break;
        }
        result.push({
          id: op.tagId,
          requestId: requestIdNum,
          name: op.data.name,
        });
        break;
      }
      case 'update': {
        if (op.tagId === undefined || !op.data?.name) {
          break;
        }
        result = result.map(tag =>
          tag.id === op.tagId ? { ...tag, name: op.data!.name } : tag,
        );
        break;
      }
      case 'delete': {
        if (op.tagId === undefined) {
          break;
        }
        result = result.filter(tag => tag.id !== op.tagId);
        break;
      }
    }
  }

  return result;
}

export async function getOfflineLocationTagList(
  requestId: string,
): Promise<LocationTag[]> {
  const [base, ops] = await Promise.all([
    getCachedLocationTagList(requestId),
    getPendingLocationTagOpsForRequest(requestId),
  ]);
  return applyPendingLocationTagOperations(requestId, base, ops);
}

export function isLocationTagNameConflictError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return (
      error.status === 400 &&
      error.message.toLowerCase().includes('already used')
    );
  }
  if (!(error instanceof Error)) {
    return false;
  }
  return error.message.toLowerCase().includes('already used');
}
